import { Validators } from '@angular/forms';
import { ReportFormat } from '@assetaccountant/api/models/reports/report-format';
import { RegimeApiModel } from '@assetaccountant/core/models/regime-api-model';
import { TypedFormControl, TypedFormGroup } from '@assetaccountant/core/utils/typed-forms';
import { ReportType } from './report-type';

export type ReportSettings =
  | (ReportSettingsBase<Exclude<ReportDateType, 'DURATION'>> & {
      durationsInYears?: undefined;
    })
  | (ReportSettingsBase<'DURATION'> & {
      durationsInYears?: ReportDuration[];
    });

export type ReportDateType = 'START' | 'END' | 'ENDRESTRICT' | 'RANGE' | 'DURATION' | 'NONE';

export interface ReportDuration {
  label: string;
  value: number;
  pdfAvailable: boolean;
}

interface ReportSettingsBase<TDateType extends ReportDateType> {
  readonly dateType: TDateType;
  regimeFilter?: (regime: RegimeApiModel) => boolean;
  forceAllRegimes?: boolean;
  disablePdf?: boolean;
  displayPdfAssetLimitExceeded?: boolean;
  forceCsv?: boolean;
  forceXlsx?: boolean;
  shouldShowClassifiedDepreciation?: boolean;
  shouldShowIncludeAdditionalDisposalInfo?: boolean;
  hasField?: 'REQUIRED' | 'OPTIONAL';
  hasExternalEntityCode?: boolean;
  disableConsolidationActions?: boolean;
}

export function forCsvOrXlsx<T extends { format: ReportFormat }>(
  command: T,
  forceXlsx: boolean,
): T & { format: ReportFormat.Csv | ReportFormat.Xlsx } {
  let format = command.format;
  if (format === ReportFormat.Pdf) {
    format = forceXlsx ? ReportFormat.Xlsx : ReportFormat.Csv;
  }

  return { ...command, format };
}

export function getReportSettings(
  reportType: ReportType | null | undefined,
  {
    pdfAssetLimitExceeded,
  }: {
    pdfAssetLimitExceeded: boolean;
  },
): ReportSettings {
  switch (reportType) {
    case ReportType.DepreciationForecast: {
      return {
        dateType: 'DURATION',
        durationsInYears: [
          createDuration(1, true),
          createDuration(10, true),
          ...[20, 30, 40, 50, 60, 70, 80, 90, 100].map(n => createDuration(n)),
        ],
        hasField: 'OPTIONAL',
      };
    }
    case ReportType.TaxAudit:
      return {
        dateType: 'RANGE',
        regimeFilter: regime => !regime.isAccounts,
        disablePdf: true,
      };

    case ReportType.AssetExport:
      return {
        dateType: 'RANGE',
        disablePdf: true,
        shouldShowIncludeAdditionalDisposalInfo: true,
      };

    case ReportType.AssetGroupSummary:
      return {
        dateType: 'RANGE',
        shouldShowClassifiedDepreciation: true,
      };

    case ReportType.AssetSummary:
      return {
        disablePdf: pdfAssetLimitExceeded,
        displayPdfAssetLimitExceeded: pdfAssetLimitExceeded,
        dateType: 'RANGE',
        shouldShowClassifiedDepreciation: true,
      };

    case ReportType.RevaluationMovements:
      return {
        dateType: 'RANGE',
        regimeFilter: regime => regime.isAccounts,
        disablePdf: true,
      };
    case ReportType.Impairments:
      return {
        dateType: 'END',
        regimeFilter: regime => regime.isAccounts,
      };

    case ReportType.AssetSchedule:
      return {
        disablePdf: pdfAssetLimitExceeded,
        displayPdfAssetLimitExceeded: pdfAssetLimitExceeded,
        dateType: 'RANGE',
      };

    case ReportType.ClassificationSplit:
      return {
        disablePdf: true,
        dateType: 'END',
        hasField: 'REQUIRED',
      };

    default:
      return {
        dateType: 'RANGE',
      };
  }
}

type ReportFormControls = {
  periodStartDate: TypedFormControl<string>;
  periodEndDate: TypedFormControl<string>;
  periodRestrictionDate?: TypedFormControl<string>;
  durationInYears: TypedFormControl<number>;
  regimeId: TypedFormControl<number>;
  format: TypedFormControl<ReportFormat>;
  includeAdditionalDisposalInfo: TypedFormControl<boolean>;
  shouldIncludeClassifiedDepreciation?: TypedFormControl<boolean>;
};

export interface ReportComponentBase<TDateType extends ReportDateType> {
  regimes: RegimeApiModel[];
  dateType: TDateType;
  disablePdf: boolean;
  durationDisablePdf: boolean;
  forceCsv?: boolean;
  forceXlsx?: boolean;
  limitStartDate: boolean;
  filteredRegimes?: RegimeApiModel[];
  durations?: ReportDuration[];
  shouldShowIncludeAdditionalDisposalInfo: boolean;
}

export interface ConsolidatedReportComponent
  extends ReportComponentBase<'RANGE' | 'DURATION' | 'START'> {
  form: TypedFormGroup<ReportFormControls>;
  shouldShowConsolidationActions: boolean;
}
export interface ReportComponent extends ReportComponentBase<ReportDateType> {
  form: TypedFormGroup<
    ReportFormControls & {
      groupingFieldId: TypedFormControl<string>;
      externalEntityCode: TypedFormControl<string>;
    }
  >;
  forceAllRegimes: boolean;
  pdfAssetLimitExceeded?: boolean;
  displayPdfAssetLimitExceeded?: boolean;
  shouldShowClassifiedDepreciation: boolean;
  hasField?: 'OPTIONAL' | 'REQUIRED';
  hasExternalEntityCode?: boolean;
}

export function updateComponent<TDateType extends ReportDateType>(
  component: (ReportComponent | ConsolidatedReportComponent) & { dateType: TDateType },
  settings: ReportSettings & { dateType: TDateType },
) {
  // Dates
  component.dateType = settings.dateType;
  if ('shouldShowClassifiedDepreciation' in component) {
    component.shouldShowClassifiedDepreciation = settings.shouldShowClassifiedDepreciation || false;
  }
  component.shouldShowIncludeAdditionalDisposalInfo =
    settings.shouldShowIncludeAdditionalDisposalInfo || false;
  component.durationDisablePdf = false;
  component.limitStartDate = false;
  toggle(
    component.form.controls.periodStartDate,
    ['START', 'RANGE', 'DURATION'].includes(settings.dateType),
  );
  toggle(
    component.form.controls.periodEndDate,
    ['END', 'RANGE', 'ENDRESTRICT'].includes(settings.dateType),
  );
  toggle(
    component.form.controls.periodRestrictionDate,
    ['ENDRESTRICT'].includes(settings.dateType),
  );
  toggle(component.form.controls.durationInYears, ['DURATION'].includes(settings.dateType));
  component.durations = settings.durationsInYears?.map(({ value, pdfAvailable }) => ({
    label: value < 2 ? `${(value * 12).toFixed(0)} Months` : `${value} Years`,
    value,
    pdfAvailable,
  }));
  if (component.durations?.length === 1) {
    component.form.controls.durationInYears.setValue(component.durations[0].value);
  }

  // force the display to show "All" for regimes
  if ('forceAllRegimes' in component) {
    component.forceAllRegimes = settings.forceAllRegimes ?? false;
  }

  // Consolidation actions
  if ('shouldShowConsolidationActions' in component) {
    component.shouldShowConsolidationActions = settings.disableConsolidationActions !== true;
  }

  const pdfWasDisabled = component.disablePdf || component.durationDisablePdf;

  // PDF - don't toggle because we still want to send the format
  component.disablePdf =
    (settings.disablePdf ?? false) ||
    !!(
      'shouldShowClassifiedDepreciation' in component &&
      component.shouldShowClassifiedDepreciation &&
      component.form.controls.shouldIncludeClassifiedDepreciation?.value
    );
  if (component.disablePdf) {
    component.form.controls.format.setValue(ReportFormat.Csv);
  } else if (pdfWasDisabled) {
    component.form.controls.format.setValue(ReportFormat.Pdf);
  }
  if ('displayPdfAssetLimitExceeded' in component) {
    component.displayPdfAssetLimitExceeded = settings.displayPdfAssetLimitExceeded ?? false;
  }

  component.forceCsv = settings.forceCsv ?? false;
  component.forceXlsx = settings.forceXlsx ?? false;

  // Regimes
  component.filteredRegimes = settings.regimeFilter
    ? component.regimes.filter(settings.regimeFilter)
    : undefined;
  toggle(component.form.controls.regimeId, component.filteredRegimes?.length !== 0);
  if (component.filteredRegimes?.length === 1) {
    component.form.controls.regimeId.setValue(component.filteredRegimes[0].id);
  }

  // External Entity Code
  if ('hasField' in component) {
    component.hasField = settings.hasField;
    toggle(component.form.controls.groupingFieldId, !!component.hasField);
    if (settings.hasField === 'REQUIRED') {
      component.form.controls.groupingFieldId.setValidators([Validators.required]);
    } else if (settings.hasField === 'OPTIONAL') {
      component.form.controls.groupingFieldId.clearValidators();
    }
    component.form.controls.groupingFieldId.updateValueAndValidity({ emitEvent: false });
  }

  if ('hasExternalEntityCode' in component) {
    component.hasExternalEntityCode = settings.hasExternalEntityCode ?? false;
    toggle(component.form.controls.externalEntityCode, component.hasExternalEntityCode);
  }

  component.form.controls.periodStartDate.setValidators([Validators.required]);
  component.form.controls.periodEndDate.setValidators([Validators.required]);
  component.form.controls.periodRestrictionDate?.setValidators([Validators.required]);

  component.form.controls.periodStartDate.updateValueAndValidity({ emitEvent: false });
  component.form.controls.periodEndDate.updateValueAndValidity({ emitEvent: false });
  component.form.controls.periodRestrictionDate?.updateValueAndValidity({ emitEvent: false });

  function toggle<T>(control: TypedFormControl<T> | undefined, isEnabled: boolean) {
    control?.[isEnabled ? 'enable' : 'disable']();
  }
}

function createDuration(years: number, pdfAvailable = false): ReportDuration {
  return {
    label: years < 2 ? `${(years * 12).toFixed(0)} Months` : `${years} Years`,
    value: years,
    pdfAvailable,
  };
}
